// AGC: NewPlotStep2 - Create plot - map drawing
import React, { useState, useRef } from 'react'
import { MapContainer, TileLayer, FeatureGroup, Polygon } from 'react-leaflet'
import { EditControl } from 'react-leaflet-draw'
import { Locate, Layers, Trash2 } from 'lucide-react'
import { TopBar, GlassCard, Button } from '../shared'
import { formatArea } from '@droidfarm/shared'
import { MAP_CONFIG } from '@droidfarm/shared'
import type { Screen, PlotFormData } from '../../types'
import type { LatLng, LatLngExpression } from 'leaflet'
import { PermissionBlockedModal } from '../common/PermissionBlockedModal'
import { PermissionBlockedError } from '../../services/nativeGeolocation'
import { useGpsAutoCenter } from '../../hooks/useGpsAutoCenter'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'

interface NewPlotStep2Props {
  plotData: PlotFormData
  onNavigate: (screen: Screen, data?: { plotData?: PlotFormData }) => void
  onBack: () => void
}

/**
 * AGC: NewPlotStep2 - Map drawing for plot boundaries
 */
export const NewPlotStep2: React.FC<NewPlotStep2Props> = ({ plotData, onNavigate, onBack }) => {
  const [coordinates, setCoordinates] = useState<[number, number][]>(plotData.coordinates || [])
  const [calculatedArea, setCalculatedArea] = useState<number>(plotData.area || 0)
  const [isSatellite, setIsSatellite] = useState(false)
  const [showPermBlocked, setShowPermBlocked] = useState(false)
  const mapRef = useRef<any>(null)
  const featureGroupRef = useRef<any>(null)

  // AGC: Auto-center the map on the user's current GPS location once on mount
  // AGC: when no existing polygon coordinates were passed in. If the user is
  // AGC: editing an existing plot (coordinates already present), keep the map
  // AGC: framed on those coords instead of yanking it to the device location.
  useGpsAutoCenter({
    enabled: !plotData.coordinates || plotData.coordinates.length === 0,
    onLocate: (coords) => {
      if (mapRef.current) {
        mapRef.current.setView([coords.latitude, coords.longitude], 16)
      }
    },
  })

  // AGC: Calculate polygon area in hectares
  const calculatePolygonArea = (coords: [number, number][]): number => {
    if (coords.length < 3) return 0
    
    // AGC: Using Shoelace formula with Earth radius correction
    let area = 0
    const n = coords.length
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      area += coords[i][1] * coords[j][0]
      area -= coords[j][1] * coords[i][0]
    }
    
    area = Math.abs(area) / 2
    
    // AGC: Convert to hectares (approximate)
    // This is a simplified calculation - in production, use proper geodesic calculation
    const latMid = coords.reduce((sum, c) => sum + c[0], 0) / n
    const metersPerDegreeLat = 111320
    const metersPerDegreeLng = 111320 * Math.cos(latMid * Math.PI / 180)
    
    const areaInSquareMeters = area * metersPerDegreeLat * metersPerDegreeLng
    return areaInSquareMeters / 10000 // Convert to hectares
  }

  // AGC: Handle polygon creation
  const handleCreated = (e: any) => {
    const layer = e.layer
    const latlngs = layer.getLatLngs()[0] as LatLng[]
    const coords: [number, number][] = latlngs.map((ll: LatLng) => [ll.lat, ll.lng])
    
    setCoordinates(coords)
    const area = calculatePolygonArea(coords)
    setCalculatedArea(area)
  }

  // AGC: Handle polygon edit
  const handleEdited = (e: any) => {
    const layers = e.layers
    layers.eachLayer((layer: any) => {
      const latlngs = layer.getLatLngs()[0] as LatLng[]
      const coords: [number, number][] = latlngs.map((ll: LatLng) => [ll.lat, ll.lng])
      
      setCoordinates(coords)
      const area = calculatePolygonArea(coords)
      setCalculatedArea(area)
    })
  }

  // AGC: Handle polygon delete
  const handleDeleted = () => {
    setCoordinates([])
    setCalculatedArea(0)
  }

  // AGC: Go to user location via nativeGeolocation (Capacitor on native, browser on web)
  const goToMyLocation = async () => {
    try {
      const { getCurrentPosition } = await import('../../services/nativeGeolocation')
      const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      const { latitude, longitude } = coords
      if (mapRef.current) {
        mapRef.current.setView([latitude, longitude], 16)
      }
    } catch (err: any) {
      console.error('AGC: Geolocation error:', err)
      if (err instanceof PermissionBlockedError) {
        setShowPermBlocked(true)
      } else {
        alert(err?.message || 'Não foi possível obter sua localização.')
      }
    }
  }

  // AGC: Clear drawing
  const clearDrawing = () => {
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers()
    }
    setCoordinates([])
    setCalculatedArea(0)
  }

  // AGC: Handle next step
  const handleNext = () => {
    const updatedPlotData: PlotFormData = {
      ...plotData,
      coordinates,
      area: calculatedArea || plotData.area,
    }
    onNavigate('plot-summary', { plotData: updatedPlotData })
  }

  // AGC: Tile layer URL based on mode
  const tileUrl = isSatellite ? MAP_CONFIG.satelliteTileUrl : MAP_CONFIG.tileUrl

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex flex-col">
      {/* AGC: Top Bar */}
      <TopBar 
        title="Desenhar Talhão"
        subtitle="Passo 2 de 2"
        onBack={onBack}
      />

      {/* AGC: Progress indicator */}
      <div className="px-4 py-3">
        <div className="flex gap-2">
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
        </div>
      </div>

      {/* AGC: Map Container */}
      <div className="flex-1 relative">
        <MapContainer
          center={MAP_CONFIG.defaultCenter}
          zoom={MAP_CONFIG.defaultZoom}
          className="h-full w-full"
          ref={mapRef}
        >
          <TileLayer
            url={tileUrl}
            attribution={MAP_CONFIG.attribution}
          />
          
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topright"
              onCreated={handleCreated}
              onEdited={handleEdited}
              onDeleted={handleDeleted}
              draw={{
                rectangle: false,
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
                polygon: {
                  allowIntersection: false,
                  showArea: true,
                  shapeOptions: {
                    color: '#0B5F4A',
                    fillColor: '#0B5F4A',
                    fillOpacity: 0.3,
                  },
                },
              }}
            />
            
            {/* AGC: Show existing polygon if editing */}
            {plotData.coordinates && plotData.coordinates.length > 0 && (
              <Polygon
                positions={plotData.coordinates as LatLngExpression[]}
                pathOptions={{
                  color: '#0B5F4A',
                  fillColor: '#0B5F4A',
                  fillOpacity: 0.3,
                }}
              />
            )}
          </FeatureGroup>
        </MapContainer>

        {/* AGC: Map Controls */}
        <div className="absolute bottom-32 right-4 flex flex-col gap-2 z-[1000]">
          <button
            onClick={goToMyLocation}
            className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
            title="Minha localização"
          >
            <Locate className="w-5 h-5 text-[#0B5F4A]" />
          </button>
          
          <button
            onClick={() => setIsSatellite(!isSatellite)}
            className={`w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-colors ${
              isSatellite ? 'bg-[#0B5F4A] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title={isSatellite ? 'Mapa normal' : 'Satélite'}
          >
            <Layers className="w-5 h-5" />
          </button>

          {coordinates.length > 0 && (
            <button
              onClick={clearDrawing}
              className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-red-50 transition-colors"
              title="Limpar desenho"
            >
              <Trash2 className="w-5 h-5 text-red-500" />
            </button>
          )}
        </div>

        {/* AGC: Area Display */}
        <div className="absolute top-4 left-4 z-[1000]">
          <GlassCard className="shadow-lg">
            <div className="text-center">
              <p className="text-xs text-gray-500">Área calculada</p>
              <p className="text-xl font-bold text-[#0B5F4A]">
                {calculatedArea > 0 ? formatArea(calculatedArea) : '—'}
              </p>
            </div>
          </GlassCard>
        </div>

        {/* AGC: Instructions */}
        {coordinates.length === 0 && (
          <div className="absolute bottom-32 left-4 right-16 z-[1000]">
            <GlassCard className="bg-blue-50 border-blue-200 shadow-lg">
              <div className="flex items-start gap-2">
                <span className="text-lg">✏️</span>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Desenhe os limites do talhão
                  </p>
                  <p className="text-xs text-blue-600">
                    Clique no ícone de polígono no canto superior direito para começar
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>
        )}
      </div>

      {/* AGC: Footer with button */}
      <div className="p-4 bg-white border-t border-gray-200">
        <Button 
          fullWidth 
          onClick={handleNext}
          disabled={coordinates.length < 3}
        >
          {coordinates.length >= 3 ? 'Revisar e Confirmar' : 'Desenhe o talhão para continuar'}
        </Button>
      </div>

      {/* AGC: Blocked permission modal */}
      <PermissionBlockedModal
        isOpen={showPermBlocked}
        permissionType="location"
        onClose={() => setShowPermBlocked(false)}
      />
    </div>
  )
}

export default NewPlotStep2







